/**
 * Smart Caching Service with TTL and Market Hours Awareness
 *
 * Features:
 * - Time-based TTL (different for market hours vs after hours)
 * - AsyncStorage-based persistence
 * - Stale-while-revalidate pattern support
 * - Automatic cache expiration
 */

import { storageService } from '@services/storage';

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  ttl: number;
}

interface CacheOptions {
  ttl?: number; // Time to live in milliseconds
  forceRefresh?: boolean; // Bypass cache
}

/**
 * Check if US stock market is currently open
 * Market hours: 9:30 AM - 4:00 PM ET, Monday-Friday
 */
export function isMarketOpen(): boolean {
  const now = new Date();
  const etTime = new Date(now.toLocaleString('en-US', { timeZone: 'America/New_York' }));

  const day = etTime.getDay(); // 0 = Sunday, 6 = Saturday
  const hour = etTime.getHours();
  const minute = etTime.getMinutes();
  const timeInMinutes = hour * 60 + minute;

  // Monday-Friday (1-5)
  const isWeekday = day >= 1 && day <= 5;

  // 9:30 AM = 570 minutes, 4:00 PM = 960 minutes
  const marketStart = 9 * 60 + 30; // 9:30 AM
  const marketEnd = 16 * 60; // 4:00 PM

  return isWeekday && timeInMinutes >= marketStart && timeInMinutes < marketEnd;
}

/**
 * Get appropriate cache TTL based on market status
 */
export function getSmartTTL(customTTL?: number): number {
  if (customTTL) return customTTL;

  if (isMarketOpen()) {
    // During market hours: Cache for 30 seconds (prices change rapidly)
    return 30 * 1000;
  } else {
    // After hours: Cache for 5 minutes (prices don't change)
    return 5 * 60 * 1000;
  }
}

/**
 * Smart Cache Service
 */
class SmartCacheService {
  private prefix = '@trading_cache:';

  /**
   * Generate cache key
   */
  private getKey(key: string): string {
    return `${this.prefix}${key}`;
  }

  /**
   * Set value in cache with TTL
   */
  async set<T>(key: string, data: T, options?: CacheOptions): Promise<void> {
    try {
      const ttl = options?.ttl || getSmartTTL();

      const entry: CacheEntry<T> = {
        data,
        timestamp: Date.now(),
        ttl,
      };

      await storageService.setItem(this.getKey(key), JSON.stringify(entry));
      console.log(`✅ Cached: ${key} (TTL: ${Math.round(ttl / 1000)}s)`);
    } catch (error) {
      console.error(`Cache set error for ${key}:`, error);
    }
  }

  /**
   * Get value from cache
   * Returns null if expired or not found
   */
  async get<T>(key: string): Promise<T | null> {
    try {
      const cached = await storageService.getItem(this.getKey(key));

      if (!cached) {
        console.log(`❌ Cache miss: ${key}`);
        return null;
      }

      const entry: CacheEntry<T> = JSON.parse(cached);
      const age = Date.now() - entry.timestamp;

      // Check if expired
      if (age > entry.ttl) {
        console.log(`⏰ Cache expired: ${key} (age: ${Math.round(age / 1000)}s)`);
        await this.delete(key); // Clean up
        return null;
      }

      console.log(
        `✅ Cache hit: ${key} (age: ${Math.round(age / 1000)}s, ttl: ${Math.round(entry.ttl / 1000)}s)`
      );
      return entry.data;
    } catch (error) {
      console.error(`Cache get error for ${key}:`, error);
      return null;
    }
  }

  /**
   * Check if cache exists and is still valid
   */
  async has(key: string): Promise<boolean> {
    const data = await this.get(key);
    return data !== null;
  }

  /**
   * Get cache age in milliseconds
   * Returns null if not cached
   */
  async getAge(key: string): Promise<number | null> {
    try {
      const cached = await storageService.getItem(this.getKey(key));
      if (!cached) return null;

      const entry: CacheEntry<any> = JSON.parse(cached);
      return Date.now() - entry.timestamp;
    } catch (error) {
      return null;
    }
  }

  /**
   * Check if cache is stale (approaching expiration)
   * Returns true if cache exists and is > 80% of TTL age
   */
  async isStale(key: string): Promise<boolean> {
    try {
      const cached = await storageService.getItem(this.getKey(key));
      if (!cached) return false;

      const entry: CacheEntry<any> = JSON.parse(cached);
      const age = Date.now() - entry.timestamp;

      // Stale if > 80% of TTL
      return age > entry.ttl * 0.8;
    } catch (error) {
      return false;
    }
  }

  /**
   * Delete specific cache entry
   */
  async delete(key: string): Promise<void> {
    try {
      await storageService.removeItem(this.getKey(key));
      console.log(`🗑️ Cache deleted: ${key}`);
    } catch (error) {
      console.error(`Cache delete error for ${key}:`, error);
    }
  }

  /**
   * Clear all cache entries
   */
  async clearAll(): Promise<void> {
    try {
      const keys = await storageService.getAllKeys();
      const cacheKeys = keys.filter(key => key.startsWith(this.prefix));
      await storageService.multiRemove(cacheKeys);
      console.log(`🗑️ Cleared ${cacheKeys.length} cache entries`);
    } catch (error) {
      console.error('Cache clearAll error:', error);
    }
  }

  /**
   * Get all cache keys
   */
  async getAllKeys(): Promise<string[]> {
    try {
      const keys = await storageService.getAllKeys();
      return keys
        .filter(key => key.startsWith(this.prefix))
        .map(key => key.replace(this.prefix, ''));
    } catch (error) {
      console.error('Cache getAllKeys error:', error);
      return [];
    }
  }

  /**
   * Get cache statistics
   */
  async getStats(): Promise<{
    totalEntries: number;
    totalSize: number;
    oldestEntry: number | null;
    newestEntry: number | null;
  }> {
    try {
      const keys = await this.getAllKeys();
      let totalSize = 0;
      let oldestTimestamp: number | null = null;
      let newestTimestamp: number | null = null;

      for (const key of keys) {
        const cached = await AsyncStorage.getItem(this.getKey(key));
        if (cached) {
          totalSize += cached.length;
          const entry: CacheEntry<any> = JSON.parse(cached);

          if (!oldestTimestamp || entry.timestamp < oldestTimestamp) {
            oldestTimestamp = entry.timestamp;
          }
          if (!newestTimestamp || entry.timestamp > newestTimestamp) {
            newestTimestamp = entry.timestamp;
          }
        }
      }

      return {
        totalEntries: keys.length,
        totalSize,
        oldestEntry: oldestTimestamp,
        newestEntry: newestTimestamp,
      };
    } catch (error) {
      console.error('Cache getStats error:', error);
      return {
        totalEntries: 0,
        totalSize: 0,
        oldestEntry: null,
        newestEntry: null,
      };
    }
  }
}

// Export singleton instance
export const smartCache = new SmartCacheService();
